import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useFormContext } from 'react-hook-form-mui';
import DataContainer from '../../../../Utilities/DataContainer';
import RenderSelectField from '../../../../Utilities/Forms/RenderSelectField';
import RenderTextField from '../../../../Utilities/Forms/RenderTextField';
import ValidatedSelectElement from '../../../../Utilities/Forms/ValidatedSelectElement';
import ValidatedTextField from '../../../../Utilities/Forms/ValidatedTextField';
import monthDropdown from '../../../../Utilities/Constants/MonthDropdown';
import yesNoDropdown from '../../../../Utilities/Constants/YesNoDropdown';

interface IProps {
    isReadOnly: boolean
    isResidential: boolean
}
export default function TravelPlanPublicTransportV2({ isReadOnly, isResidential }: IProps) {
    const { getValues } = useFormContext();
    const formValues = getValues();
    const hasValues = (fields: string[]) => fields.some(field => formValues[field]) || !isReadOnly;

    return (
        <>
            <Grid container alignItems="flex-start">
                <Grid size={4} container>
                    {hasValues(["busStopsNearby", "realTimeInfo", "timetableDisplay", "busShelter", "busSeating", "busDroppedKerbs", "busStopMonth", "busStopYear", "busStopBeforeSite"]) && (
                        <Grid container size={12} >
                            <DataContainer title={"Bus Stop Details"}>
                                <Stack spacing={1}>
                                    <RenderSelectField name="busStopsNearby" label="Are there any safely accessible public bus stops within 400 metres walking distance of any of the site's site's access points (or within the site)?" readOnly={isReadOnly} options={yesNoDropdown} />
                                    <Stack spacing={1}>
                                        <RenderSelectField label={'Real time information'} name={'realTimeInfo'} readOnly={isReadOnly} options={yesNoDropdown} />
                                        <RenderSelectField label={'Timetable display'} name={'timetableDisplay'} readOnly={isReadOnly} options={yesNoDropdown} />
                                        <RenderSelectField label={'Shelter'} name={'busShelter'} readOnly={isReadOnly} options={yesNoDropdown} />
                                        <RenderSelectField label={'Seating'} name={'busSeating'} readOnly={isReadOnly} options={yesNoDropdown} />
                                        <RenderSelectField label={'Dropped kerbs'} name={'busDroppedKerbs'} readOnly={isReadOnly} options={yesNoDropdown} />
                                    </Stack>
                                    <Grid size={12}>
                                        <Typography variant={"body2"}>Date Implemented</Typography>
                                    </Grid>
                                    <Stack direction="row" spacing={1}>
                                        <Grid size={6}>
                                            <ValidatedSelectElement name={'busStopMonth'} options={monthDropdown} readOnly={isReadOnly} />
                                        </Grid>
                                        <Grid size={6}>
                                            <ValidatedTextField name={'busStopYear'} readOnly={isReadOnly} />
                                        </Grid>
                                    </Stack>
                                    <RenderSelectField name="busStopBeforeSite" label="In place prior to the development of the site or otherwise otherwise independent of it (i.e. not a Travel Plan element)" readOnly={isReadOnly} options={yesNoDropdown} />
                                </Stack>
                            </DataContainer>
                        </Grid>
                    )}

                    {hasValues(["newBusServices", "newBusServicesMonth", "newBusServicesYear", "newBusBeforeSite"]) && (
                        <Grid container size={12}>
                            <DataContainer title={"New Bus Service Details"}>
                                <Stack spacing={1}>
                                    <RenderSelectField name="newBusServices" label="Have any new bus services been introduced in the local area, and/or have existing services been improved?" readOnly={isReadOnly} options={yesNoDropdown} />
                                    <Grid size={12}>
                                        <Typography variant={"body2"}>Date Implemented</Typography>
                                    </Grid>
                                    <Stack direction="row" spacing={1}>
                                        <Grid size={6}>
                                            <ValidatedSelectElement name={'newBusServicesMonth'} options={monthDropdown} readOnly={isReadOnly} />
                                        </Grid>
                                        <Grid size={6}>
                                            <ValidatedTextField name={'newBusServicesYear'} readOnly={isReadOnly} />
                                        </Grid>
                                    </Stack>
                                    <RenderSelectField name="newBusBeforeSite" label="In place prior to the development of the site or otherwise otherwise independent of it (i.e. not a Travel Plan element)" readOnly={isReadOnly} options={yesNoDropdown} />
                                </Stack>
                            </DataContainer>
                        </Grid>
                    )}
                </Grid>

                <Grid size={4} container>
                    {hasValues(["safeRoutesBus", "safeRoutesBusMonth", "safeRoutesBusYear", "safeRoutesBusBeforeSite"]) && (
                        <Grid container size={12} >
                            <DataContainer title={"Bus Route Details"}>
                                <Stack spacing={1}>
                                    <RenderSelectField name="safeRoutesBus" label="Are there accessible and well-lit pedestrian routes to local bus/tram stops within 400 metres walking distance of the site?" readOnly={isReadOnly} options={yesNoDropdown} />
                                    <Grid size={12}>
                                        <Typography variant={"body2"}>Date Implemented</Typography>
                                    </Grid>
                                    <Stack direction="row" spacing={1}>
                                        <Grid size={6}>
                                            <ValidatedSelectElement name={'safeRoutesBusMonth'} options={monthDropdown} readOnly={isReadOnly} />
                                        </Grid>
                                        <Grid size={6}>
                                            <ValidatedTextField name={'safeRoutesBusYear'} readOnly={isReadOnly} />
                                        </Grid>
                                    </Stack>
                                    <RenderSelectField name="safeRoutesBusBeforeSite" label="In place prior to the development of the site or otherwise otherwise independent of it (i.e. not a Travel Plan element)" readOnly={isReadOnly} options={yesNoDropdown} />
                                </Stack>
                            </DataContainer>
                        </Grid>
                    )}
                    {hasValues(["safeRoutesRail", "safeRoutesRailYear", "safeRoutesRailMonth", "safeRoutesRailBeforeSite"]) && (
                        <Grid container size={12} >
                            <DataContainer title={"Rail Route Details"}>
                                <Stack spacing={1}>
                                    <RenderSelectField name="safeRoutesRail" label="Are there accessible and well-lit pedestrian routes to rail stations within a 1-kilometre walking distance of the site?" readOnly={isReadOnly} options={yesNoDropdown} />
                                    <Grid size={12}>
                                        <Typography variant={"body2"}>Date Implemented</Typography>
                                    </Grid>
                                    <Stack direction="row" spacing={1}>
                                        <Grid size={6}>
                                            <ValidatedSelectElement name={'safeRoutesRailMonth'} options={monthDropdown} readOnly={isReadOnly} />
                                        </Grid>
                                        <Grid size={6}>
                                            <ValidatedTextField name={'safeRoutesRailYear'} readOnly={isReadOnly} />
                                        </Grid>
                                    </Stack>
                                    <RenderSelectField name="safeRoutesRailBeforeSite" label="In place prior to the development of the site or otherwise otherwise independent of it (i.e. not a Travel Plan element)" readOnly={isReadOnly} options={yesNoDropdown} />
                                </Stack>
                            </DataContainer>
                        </Grid>
                    )}
                    <Grid container size={12} >
                        <Grid size={12}>
                            <DataContainer title={"Additional Comments"}>
                                <RenderTextField name="ptComments" label="Specify details of all Travel Plan elements identified in this section, along with any other public transport features not already covered." readOnly={isReadOnly} multiLine={true} rows={4} labelSize={12} inputSize={12} alwaysShow={true} />
                            </DataContainer>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid size={4} container>
                    {hasValues(["ptInfoProvided", "residentWelcomePack", "staffInfoPack", "noticeboard", "newsletters", "journeyPlanning", "travelWebsite", "ptTimetables", "otherPTInfo", "otherPTInfoText", "ptInfoMonth", "ptInfoYear"]) && (
                        <Grid container size={12} >
                            <DataContainer title={isResidential ? "Information Provided to Residents Details" : "Public Transport Information Details"}>
                                <Stack spacing={1}>
                                    <RenderSelectField
                                        name="ptInfoProvided"
                                        label={isResidential
                                            ? "Are residents provided with a 'welcome pack' (or similar) that includes the promotion of local public transport services, and/or are any other specific public transport information services available to residents?"
                                            : "Is information provided to the Travel Plan target group (e.g. staff, visitors, etc.) on local public transport services, and/or are any other specific public transport information services available?"}
                                        readOnly={isReadOnly}
                                        options={yesNoDropdown}
                                    />
                                    <Stack spacing={1}>
                                        {isResidential
                                            ? <RenderSelectField label={"Residents' welcome pack"} name={'residentWelcomePack'} readOnly={isReadOnly} options={yesNoDropdown} />
                                            : <RenderSelectField label={'Staff information packs'} name={'staffInfoPack'} readOnly={isReadOnly} options={yesNoDropdown} />}
                                        <RenderSelectField label={'Noticeboard'} name={'noticeboard'} readOnly={isReadOnly} options={yesNoDropdown} />
                                        <RenderSelectField label={'Newsletters'} name={'newsletters'} readOnly={isReadOnly} options={yesNoDropdown} />
                                        <RenderSelectField label={'Personalised journey planning/travel assistance'} name={'journeyPlanning'} readOnly={isReadOnly} options={yesNoDropdown} />
                                        {isResidential && <RenderSelectField label={"Development's travel website"} name={'travelWebsite'} readOnly={isReadOnly} options={yesNoDropdown} />}
                                        <RenderSelectField label={'Public transport service timetables (on paper or online)'} name={'ptTimetables'} readOnly={isReadOnly} options={yesNoDropdown} />
                                        <RenderSelectField label={'Other (specify)'} name={'otherPTInfo'} readOnly={isReadOnly} options={yesNoDropdown} />
                                        <RenderTextField label={''} name={'otherPTInfoText'} readOnly={isReadOnly} />
                                    </Stack>
                                    <Grid size={12}>
                                        <Typography variant={"body2"}>Date Implemented</Typography>
                                    </Grid>
                                    <Stack direction="row" spacing={1}>
                                        <Grid size={6}>
                                            <ValidatedSelectElement name={'ptInfoMonth'} options={monthDropdown} readOnly={isReadOnly} />
                                        </Grid>
                                        <Grid size={6}>
                                            <ValidatedTextField name={'ptInfoYear'} readOnly={isReadOnly} />
                                        </Grid>
                                    </Stack>
                                </Stack>
                            </DataContainer>
                        </Grid>
                    )}

                </Grid>
            </Grid>
        </>
    );
}
